/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { useMemo } from 'react';
import { usePaginationAndSortSlice } from '@restore/paginationAndSort/paginationAndSort.slice';
import {
  useAppDispatch as _useAppDispatch,
  useAppSelector,
} from '@hooks/redux';
import type { RootState, Store, AppDispatch } from './store';
import {
  ActionCreatorWithPayload,
  CaseReducer,
  PayloadAction,
} from '@reduxjs/toolkit';
import type { WritableDraft } from 'immer';
import { VITE_ENV } from 'src/env';
import { routesPath } from 'src/router/routes';
import { matchPath } from 'react-router-dom';
import { makeSagaDispatch, wrapLogAction } from '@dynamika-shared/utils/saga';

const shouldUseActionMetaInfo = !(
  !window.location.host.includes('localhost') || VITE_ENV === 'jest'
);

export type Resp<F extends (...args: any) => any> = Awaited<ReturnType<F>>;
export type ErrorAction = PayloadAction<Error>;

/**
 * Second argument accepted by a slice hook, forwarded to `useSelector`.
 *
 * Plain terms
 * -----------
 * When a component asks the store for several values at once, it can also say
 * how to decide whether those values really changed — otherwise the component
 * re-renders on every store update. This type describes that second argument.
 *
 * Technical
 * ---------
 * Deliberately NOT `Parameters<typeof useAppSelector>[1]`. `TypedUseSelectorHook`
 * is an overloaded interface (equality function, or options object), and
 * `Parameters<>` resolves an overloaded signature to its LAST overload only —
 * so that form silently narrowed to `UseSelectorOptions<unknown>` and rejected
 * the plain `shallowEqual` every call site actually passes, which then made the
 * whole hook return `unknown`. Take the options form from the hook and add the
 * equality-function form back; neither `EqualityFn` nor `UseSelectorOptions` is
 * re-exported from react-redux's public entry point (8.1.3), so they cannot
 * simply be imported.
 */
type SelectorOptions = NonNullable<Parameters<typeof useAppSelector>[1]>;

type SelectorEqualityFn = (a: any, b: any) => boolean;

type Comparator = SelectorEqualityFn | SelectorOptions;

type UseBound<K extends keyof RootState, S extends RootState[K], Acts> = {
  (): S;
  <U>(selector: (state: S, acts: Acts) => U, comparator?: Comparator): U;
} & S;

/**
 * wrap action creators with dispatch
 * @param {object} schema - an object with action creators
 * @param {function} dispatch - dispatch function
 * @returns {object} - an object with action creators wrapped with dispatch
 * @example
 * const actionsBefore = {
 *  increment: createAction('increment'),
 *  decrement: () => ({ type: 'decrement' }),
 * };
 *
 * const actionsAfter = createSliceDispatcher(actionsBefore, dispatch);
 *
 * actionsAfter.increment(); // dispatch({ type: 'increment' });
 *
 */
const createSliceDispatcher = <T extends object>(schema: T, dispatch: AppDispatch) => {
  const dispatchedActions: any = {};

  Object.keys(schema).forEach(key => {
    dispatchedActions[key] = (payload: any) => {
      const obj = (schema as any)[key](payload);
      dispatch(wrapLogAction(obj, 'component', shouldUseActionMetaInfo));
    };
  });

  return dispatchedActions as T;
};

/**
 * create a hook for a slice of the store with action creators bound to dispatch function and state selector function
 * @param {string} sliceName - name of the slice
 * @param {object} actions - action creators
 * @returns - a hook that returns the slice state
 * @example
 *
 *  const actions = {
 *    increment: createAction('increment'),
 *    decrement: () => ({ type: 'decrement' }),
 *  };
 *
 * const useCounter = createSliceHook('counter', actions);
 *
 * const Counter = () => {
 *   const [count, increment, decrement] = useCounter((state, actions) => [state.count, actions.increment, actions.decrement]);
 *   return (
 *    <div>
 *     <h1>{count}</h1>
 *     <button onClick={increment}>+</button>
 *     <button onClick={decrement}>-</button>
 *    </div>
 *  );
 * };
 *
 */
export function createSliceHook<K extends keyof RootState, Acts extends object>(
  sliceName: K,
  actions: Acts,
  initialState?: unknown
): UseBound<K, RootState[K], Acts> {
  return function (selector, comparator) {
    const dispatch = _useAppDispatch();

    // Memoize action creators to prevent creating new functions on every render
    const acts = useMemo(
      () => createSliceDispatcher(actions, dispatch),
      [dispatch]
    );

    // Memoize the result to prevent creating new references on every call
    const result = useAppSelector(
      (s: RootState) => {
        if (!selector) {
          return s[sliceName] ?? initialState;
        }
        return selector(s[sliceName] ?? initialState, acts);
      },
      // react-redux accepts both an equality function and an options object at
      // runtime, but exposes them as two overloads — and TypeScript cannot pick
      // an overload for a union-typed argument. Narrowing here is safe because
      // the value is passed straight through untouched.
      comparator as SelectorOptions
    );

    return result ?? initialState;
  } as UseBound<K, RootState[K], Acts>;
}

/**
 * create a selector function for a slice of the store
 * @param {string} key - name of the slice
 * @returns - a selector function
 * @example
 *   const selectCounter = createSelector('counter'); // (root) => root.counter
 *   const counter = useSelector(selectCounter);
 */
export function createSelector<K extends keyof RootState>(key: K) {
  return (root: RootState) => root[key];
}

/**
 *  create action creators for a slice of the store with action creators bound to dispatch function and state selector function
 * @returns - an object with action creators
 * @example
 * const { act, actReq, actResolve, actReject, actEmpty } = makeActionCreator<SiteSubjectsListState>();
 * createSlice({
 *   name: 'SiteSubjectsList',
 *   initialState,
 *   reducers: {
 *     // without helpers,
 *     fetchSiteSubjectsList: (state, action: PayloadAction<SiteSubjectsListFetchAction>) => {
 *       state.loading = true;
 *       state.error = undefined;
 *     },
 *     fetchSiteSubjectsListSuccess: (state, action: PayloadAction<SiteSubjectsListFetchSuccessAction]>) => {
 *       state.data = action.payload;
 *       state.loading = false;
 *       state.error = undefined;
 *     },
 *     fetchSiteSubjectsListFailure: (state, action: PayloadAction<Error>) => {
 *       state.loading = false;
 *       state.error = action.payload;
 *     },
 *     emptyAction(state, action: PayloadAction<string>) {},
 *
 *     // with helpers,
 *     fetchSiteSubjectsList: actReq<SiteSubjectsListFetchAction['payload']>(),
 *     fetchSiteSubjectsListSuccess: actResolve<SiteSubjectsListFetchSuccessAction['payload']>(),
 *     fetchSiteSubjectsListFailure: actReject(),
 *     emptyAction: actEmpty(),
 *  }
 * });
 *
 */
export function makeActionCreator<S extends Record<string, any>>() {
  /**
   *  create a reducer function for an action
   * @param fn - a function that returns a partial state
   * @returns  - a reducer function
   * @example
   * const { act } = makeActionCreator<SiteSubjectsListState>();
   * fetchSiteSubjectsSuccessList: act(({ list, key }, state) => ({ list, val: state.values[key]})),
   */
  const act = function <P>(
    fn: (p: P, s: S, t: PayloadAction<P>['type']) => Partial<S>
  ) {
    return function (state: WritableDraft<S>, action: PayloadAction<P>) {
      return { ...state, ...fn(action.payload, state, action.type) } as S;
    };
  };

  /**
   *  create a reducer function for an action
   * @param fn - a function that returns a partial state
   * @returns  - a reducer function
   * @example
   * const { actMutate } = makeActionCreator<SiteSubjectsListState>();
   * fetchSiteSubjectsSuccessList: act(({ list, key }, state) => { state.list = list; state.val = state.values[key]; }),
   */
  const actMutate = function <P>(
    fn: (p: P, s: S, t: PayloadAction<P>['type']) => void
  ) {
    return function (state: WritableDraft<S>, action: PayloadAction<P>) {
      fn(action.payload, state, action.type);
    };
  };

  /**
   *  create a reducer function for a request action
   * @param loadingKey - key to store the loading state
   * @returns - a reducer function
   */
  const actReq = <P = void>(
    loadingKey: keyof S | null = 'loading'
  ): CaseReducer<S, PayloadAction<P>> =>
    function (state) {
      return {
        ...(state as S),
        ...(loadingKey ? { [loadingKey]: true } : {}),
        error: undefined,
      } as S;
    };

  /**
   *  create a reducer function that does nothing
   * @returns - a reducer function that does nothing
   */
  const actEmpty = <P = void>(): CaseReducer<S, PayloadAction<P>> =>
    function (state) {
      return state;
    };

  /**
   *  create a reducer function for a successful action
   * @param dataKey - key to store the data
   * @param loadingKey - key to store the loading state
   * @param errorKey - key to store the error
   * @returns - a reducer function
   */
  const actResolve = <P>(
    dataKey: keyof S | null = 'data',
    loadingKey: keyof S | null = 'loading',
    errorKey: keyof S | null = 'error'
  ): CaseReducer<S, PayloadAction<P>> =>
    function (state, action) {
      return {
        ...(state as S),
        ...(dataKey ? { [dataKey]: action.payload } : {}),
        ...(loadingKey ? { [loadingKey]: false } : {}),
        ...(errorKey ? { [errorKey]: undefined } : {}),
      } as S;
    };

  /**
   *  create a reducer function for a failed action
   * @param errorKey - key to store the error
   * @param loadingKey - key to store the loading state
   * @returns - a reducer function
   */
  const actReject = <P extends Error | null | string = Error>(
    errorKey: keyof S | null = 'error',
    loadingKey: keyof S | null = 'loading'
  ): CaseReducer<S, PayloadAction<P>> =>
    function (state, action) {
      return {
        ...(state as S),
        ...(loadingKey ? { [loadingKey]: false } : {}),
        ...(errorKey ? { [errorKey]: action.payload } : {}),
      } as S;
    };

  return { act, actMutate, actReq, actResolve, actReject, actEmpty };
}

export const getUrlParams = () => {
  const match = Object.values(routesPath)
    .filter(p => {
      const m = matchPath(p, window.location.pathname);
      return m?.pathnameBase === window.location.pathname;
    })
    .map(p => matchPath(p, window.location.pathname)?.params)?.[0];

  return match;
};

export const logError = <T>(error: T) => console.error(error);

export const dispatch = makeSagaDispatch(shouldUseActionMetaInfo);

type CB<
  A extends ReturnType<ActionCreatorWithPayload<any, string>> = ReturnType<
    ActionCreatorWithPayload<any, string>
  >,
> = (a: A, s: Store) => void;

export const _listeners: Record<string, CB[]> = {};

/**
 *  subscribe to actions dispatched in the store and call a callback function when the action is dispatched
 * @param actionType - action type or action creator or array of action types or action creators
 * @param callback - callback function to be called when the action is dispatched
 * @returns - a function to unsubscribe from the action
 * @example
 * useEffect(() => {
 *   const unsubscribe = subscribeToActions(fetchSiteSubjectsList, (action, store) => {
 *     console.log(action);
 *   });
 *   return () => {
 *     unsubscribe();
 *   };
 * }, []);
 *
 * useEffect(() => {
 *   const unsubscribe = subscribeToActions('fetchSiteSubjectsList', (action, store) => {
 *     console.log(action); // { type: 'fetchSiteSubjectsList', payload: { ... } }
 *   });
 *   return () => {
 *     unsubscribe();
 *   };
 * }, []);
 *
 * useEffect(() => {
 *   return subscribeToActions([fetchSiteSubjectsList, otherAction], (action, store) => {
 *    if( action.type === fetchSiteSubjectsList.type) {
 *     console.log(action); // { type: 'fetchSiteSubjectsList', payload: { ... } }
 *    }
 *
 *   if(action.type === otherAction.type) {
 *     console.log(action); // { type: 'otherAction', payload: { ... } }
 *   });
 * }, []);
 *
 * useEffect(() => {
 *   return subscribeToActions(['fetchSiteSubjectsList', 'otherAction'], (action, store) => {
 *    if( action.type === fetchSiteSubjectsList.type) {
 *     // do something
 *    }
 *
 *   if(action.type === otherAction.type) {
 *     console.log(action);
 *   });
 * }, []);
 *
 */
export function subscribeToActions<
  A extends ActionCreatorWithPayload<any, string>,
>(actionType: A | A['type'] | A[] | A['type'][], callback: CB<ReturnType<A>>) {
  if (Array.isArray(actionType)) {
    const unsubscribes = actionType.map(t => subscribeToActions(t, callback));
    return () => {
      unsubscribes.map(u => u());
    };
  }
  const type = typeof actionType === 'string' ? actionType : actionType.type;
  _listeners[type] = _listeners[type] || [];
  _listeners[type].push(callback as any);

  return () => {
    _listeners[type] = _listeners[type].filter(l => l !== callback);
  };
}

/**
 *  wait for an action to be dispatched in the store
 * @param actionType - action type or action creator or array of action types or action creators
 * @param timeout  - time to wait for the action before rejecting the promise
 * @returns - a promise that resolves to the action
 * @example
 * await waitForActions(fetchSiteSubjectsList);
 * await waitForActions([fetchSiteSubjectsList, otherAction]);
 * await waitForActions('fetchSiteSubjectsList', 5000);
 * await waitForActions(['fetchSiteSubjectsList', 'otherAction']);
 */
export function waitForActions<A extends ActionCreatorWithPayload<any, string>>(
  actionType: A | A['type'] | A[] | A['type'][],
  timeout = 2000
) {
  return new Promise<ReturnType<A>>((resolve, reject) => {
    const unsubscribe = subscribeToActions<A>(actionType, action => {
      unsubscribe();
      resolve(action);
    });
    setTimeout(() => {
      unsubscribe();
      reject(new Error('timeout'));
    }, timeout);
  });
}

/**
 *  middleware that passes actions to subscribers in react components
 * @param store - redux store
 * @returns
 * @example
 * const store = configureStore({
 *  reducer: rootReducer,
 *  middleware: [subscribeToActionsMiddleware],
 * });
 *
 */
export const subscribeToActionsMiddleware =
  (store: any) => (next: any) => (action: any) => {
    if (_listeners[action.type]) {
      _listeners[action.type].forEach(l => l(action, store));
    }
    return next(action);
  };

export function makeTestLogger(logsEnabled = false) {
  const logs: any[] = [];
  const logsMiddleware = (store: any) => (next: any) => (action: any) => {
    if (logsEnabled) {
      logs.push(action);
    }
    return next(action);
  };

  const showReduxLogs = () => {
    if (!logsEnabled) {
      console.log('=========logs are disabled==============');
      return;
    }
    console.log('==========REDUX===========');
    console.log(logs);
    console.log('===========END============');
  };
  return {
    showReduxLogs,
    logsMiddleware,
  };
}

export const usePaginationAndSort = <
  K extends keyof RootState['paginationAndSort']['lists'],
>(
  l: K
) => {
  return usePaginationAndSortSlice((s, a) => {
    return {
      ...s.lists[l],
      setSortBy: a.setSortBy,
      setCurrentPage: a.setCurrentPage,
      resetListStateByName: a.resetListStateByName,
    };
  });
};
